import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { APIError } from "@/api/APIError";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { Dialog } from "@/components/ui/Dialog";

type HasID = { id: string };

export type ColumnDef<T> = {
    key: string;
    label: string;
    render?: (value: unknown, row: T) => React.ReactNode;
    sortable?: boolean;
    defaultHidden?: boolean;
    required?: boolean;
    width?: string;
};

export type ActionDef<T> = {
    label: string | ((row: T) => string);
    icon?: React.ReactNode | ((row: T) => React.ReactNode);
    variant?: "button" | "icon";
    onClick: (row: T) => Promise<void> | void;
    hidden?: (row: T) => boolean;
    optimisticUpdate?: (row: T) => T;
    confirm?: {
        title: string | ((row: T) => string);
        description?: string | ((row: T) => string);
        confirmLabel?: string;
        variant?: "default" | "danger" | "warning";
    };
};

export type BulkActionDef<T> = {
    label: string;
    icon?: React.ReactNode;
    onClick: (rows: T[]) => Promise<void> | void;
};

type OrderBy = { field: string; direction: "asc" | "desc" };

type GetItemsResult<T> =
    | { items: T[]; total: number; hasMore?: never }
    | { items: T[]; hasMore: boolean; total?: never };

type DataTableProps<T extends HasID> = {
    id: string;
    columns: ColumnDef<T>[];
    actions?: ActionDef<T>[];
    bulkActions?: BulkActionDef<T>[];
    getItems: (params: {
        search: string;
        page: number;
        limit: number;
        orderBy?: OrderBy;
    }) => Promise<GetItemsResult<T>>;
    onRowClick?: (row: T) => void;
    limit?: number;
    emptyState?: React.ReactNode;
    syncWithUrl?: boolean;
    padToLimit?: boolean;
    hideSearch?: boolean;
    hideColumns?: boolean;
};

export function DataTable<T extends HasID>({
    id,
    columns,
    actions = [],
    bulkActions = [],
    getItems,
    onRowClick,
    limit = 20,
    emptyState,
    syncWithUrl = false,
    padToLimit = false,
    hideSearch = false,
    hideColumns = false,
}: DataTableProps<T>) {
    const storageKey = `data-table-cols-${id}`;
    const [searchParams, setSearchParams] = useSearchParams();

    const [search, setSearch] = useState(() =>
        syncWithUrl ? (searchParams.get("search") ?? "") : ""
    );
    const [debouncedSearch] = useDebounce(search, 300);
    const [page, setPage] = useState(() =>
        syncWithUrl ? Number(searchParams.get("page") ?? 1) : 1
    );
    const [orderBy, setOrderBy] = useState<OrderBy | undefined>(() => {
        if (!syncWithUrl) return undefined;
        const field = searchParams.get("orderBy");
        const direction = searchParams.get("orderByDir") as "asc" | "desc" | null;
        return field ? { field, direction: direction ?? "asc" } : undefined;
    });
    const [items, setItems] = useState<T[]>([]);
    const [total, setTotal] = useState<number | undefined>();
    const [hasMore, setHasMore] = useState<boolean | undefined>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored) return new Set(JSON.parse(stored) as string[]);
        } catch {}
        return new Set(columns.filter(c => c.defaultHidden).map(c => c.key));
    });
    const [optimisticRows, setOptimisticRows] = useState<Record<string, T>>({});
    const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
    const [selectedIDs, setSelectedIDs] = useState<Set<string>>(new Set());
    const [bulkLoading, setBulkLoading] = useState(false);
    const [confirmState, setConfirmState] = useState<{
        row: T;
        action: ActionDef<T>;
        actionIdx: number;
    } | null>(null);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [confirmError, setConfirmError] = useState<APIError | null>(null);

    const activeRef = useRef(0);
    const getItemsRef = useRef(getItems);
    getItemsRef.current = getItems;

    const runFetch = useCallback(() => {
        const reqID = ++activeRef.current;
        const timer = setTimeout(() => {
            if (reqID === activeRef.current) setLoading(true);
        }, 150);
        setError(null);
        getItemsRef.current({ search: debouncedSearch, page, limit, orderBy })
            .then(result => {
                if (reqID !== activeRef.current) return;
                setItems(result.items);
                setTotal(result.total);
                setHasMore(result.hasMore);
                setOptimisticRows({});
                setSelectedIDs(new Set());
            })
            .catch(() => {
                if (reqID !== activeRef.current) return;
                setError("Failed to load data.");
            })
            .finally(() => {
                clearTimeout(timer);
                if (reqID !== activeRef.current) return;
                setLoading(false);
            });
    }, [debouncedSearch, page, limit, orderBy]);

    useEffect(() => { runFetch(); }, [runFetch]);

    const prevSearch = useRef(debouncedSearch);
    useEffect(() => {
        if (debouncedSearch !== prevSearch.current) {
            prevSearch.current = debouncedSearch;
            setPage(1);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        if (!syncWithUrl) return;
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            debouncedSearch ? next.set("search", debouncedSearch) : next.delete("search");
            page !== 1 ? next.set("page", String(page)) : next.delete("page");
            orderBy ? next.set("orderBy", orderBy.field) : next.delete("orderBy");
            orderBy ? next.set("orderByDir", orderBy.direction) : next.delete("orderByDir");
            return next;
        }, { replace: true });
    }, [syncWithUrl, debouncedSearch, page, orderBy, setSearchParams]);

    const toggleColumn = useCallback((key: string) => {
        const col = columns.find(c => c.key === key);
        if (col?.required) return;
        setHiddenKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch {}
            return next;
        });
    }, [storageKey, columns]);

    const visibleColumns = useMemo(() => columns.filter(c => !hiddenKeys.has(c.key)), [columns, hiddenKeys]);

    const handleSort = useCallback((key: string) => {
        setOrderBy(prev => {
            if (prev?.field === key) {
                return prev.direction === "asc" ? { field: key, direction: "desc" } : undefined;
            }
            return { field: key, direction: "asc" };
        });
        setPage(1);
    }, []);

    const displayItems = useMemo(
        () => items.map(item => optimisticRows[item.id] ?? item),
        [items, optimisticRows],
    );

    const allSelected = useMemo(
        () => items.length > 0 && items.every(i => selectedIDs.has(i.id)),
        [items, selectedIDs],
    );
    const someSelected = useMemo(
        () => !allSelected && items.some(i => selectedIDs.has(i.id)),
        [allSelected, items, selectedIDs],
    );

    const toggleSelectAll = useCallback(() => {
        setSelectedIDs(allSelected ? new Set() : new Set(items.map(i => i.id)));
    }, [allSelected, items]);

    const toggleSelect = useCallback((id: string) => {
        setSelectedIDs(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const executeAction = useCallback(async (action: ActionDef<T>, row: T, idx: number) => {
        const key = `${row.id}-${idx}`;
        setActionLoading(prev => ({ ...prev, [key]: true }));
        const currentRow = optimisticRows[row.id] ?? row;
        if (action.optimisticUpdate) {
            setOptimisticRows(prev => ({ ...prev, [row.id]: action.optimisticUpdate!(currentRow) }));
        }
        try {
            await action.onClick(currentRow);
            runFetch();
        } catch (e) {
            if (action.optimisticUpdate) {
                setOptimisticRows(prev => {
                    const next = { ...prev };
                    delete next[row.id];
                    return next;
                });
            }
            throw e;
        } finally {
            setActionLoading(prev => { const next = { ...prev }; delete next[key]; return next; });
        }
    }, [optimisticRows, runFetch]);

    const handleAction = useCallback((action: ActionDef<T>, row: T, idx: number) => {
        if (action.confirm) {
            setConfirmError(null);
            setConfirmState({ row, action, actionIdx: idx });
        } else {
            executeAction(action, row, idx);
        }
    }, [executeAction]);

    const handleConfirm = useCallback(async () => {
        if (!confirmState) return;
        setConfirmLoading(true);
        setConfirmError(null);
        try {
            await executeAction(confirmState.action, confirmState.row, confirmState.actionIdx);
            setConfirmState(null);
        } catch (e: unknown) {
            setConfirmError(e instanceof APIError ? e : new APIError("Something went wrong. Please try again."));
        } finally {
            setConfirmLoading(false);
        }
    }, [confirmState, executeAction]);

    const handleBulkAction = useCallback(async (action: BulkActionDef<T>) => {
        const selected = displayItems.filter(i => selectedIDs.has(i.id));
        setBulkLoading(true);
        try {
            await action.onClick(selected);
            runFetch();
        } catch {}
        finally { setBulkLoading(false); }
    }, [displayItems, selectedIDs, runFetch]);

    const knownTotal = total !== undefined;
    const totalPages = knownTotal ? Math.ceil(total! / limit) : undefined;
    const startRow = knownTotal && total! > 0 ? (page - 1) * limit + 1 : undefined;
    const endRow = knownTotal ? Math.min(page * limit, total!) : undefined;
    const hasBulkActions = bulkActions.length > 0;
    const hasActions = actions.length > 0;
    const selectedCount = selectedIDs.size;

    const gridStyle = useMemo<React.CSSProperties>(() => ({
        gridTemplateColumns: [
            hasBulkActions ? "32px" : null,
            ...visibleColumns.map(c => c.width ?? "1fr"),
            hasActions ? "auto" : null,
        ].filter(Boolean).join(" "),
    }), [visibleColumns, hasBulkActions, hasActions]);

    const subgridRow: React.CSSProperties = { gridColumn: "1 / -1", gridTemplateColumns: "subgrid" };
    const fullSpan: React.CSSProperties = { gridColumn: "1 / -1" };

    return (
        <>
        <div className="flex flex-col gap-3">
            {/* Top bar */}
            {(!hideSearch || !hideColumns) && (
                <div className="flex gap-3 items-center">
                    {!hideSearch && (
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                                <SearchIcon className="text-light-grey" fontSize="small" />
                            </div>
                            <input
                                className="bg-white py-2.5 px-4 pr-10 rounded-md border-2 border-light-light-grey w-full text-sm [&::-webkit-search-cancel-button]:hidden"
                                type="search"
                                placeholder="Search..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                    )}
                    {!hideColumns && <ColumnVisibilityToggle columns={columns} hiddenKeys={hiddenKeys} onToggle={toggleColumn} />}
                </div>
            )}

            {/* Bulk action bar */}
            {hasBulkActions && selectedCount > 0 && (
                <div className="flex items-center gap-3 bg-blue text-white rounded-md px-4 py-2 text-sm">
                    <span className="font-medium">{selectedCount} selected</span>
                    <div className="flex-1" />
                    {bulkActions.map((action, i) => (
                        <button
                            key={i}
                            disabled={bulkLoading}
                            onClick={() => handleBulkAction(action)}
                            className="flex items-center gap-1 px-3 py-1 rounded bg-white/20 hover:bg-white/30 disabled:opacity-50 transition-colors"
                        >
                            {action.icon}
                            {action.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Table — single grid so all rows share the same column tracks */}
            <div className="bg-white rounded-md overflow-hidden outline outline-2 outline-light-light-grey grid" style={gridStyle}>
                {/* Header */}
                <div className="grid border-b border-light-light-grey" style={subgridRow}>
                    {hasBulkActions && (
                        <div className="flex items-center justify-center p-3">
                            <IndeterminateCheckbox
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={toggleSelectAll}
                            />
                        </div>
                    )}
                    {visibleColumns.map(col => (
                        <div key={col.key} className="flex items-center gap-1 px-3 py-3 font-semibold text-sm">
                            <span>{col.label}</span>
                            {col.sortable && (
                                <button
                                    onClick={() => handleSort(col.key)}
                                    className="text-light-grey hover:text-grey ml-1"
                                >
                                    {orderBy?.field === col.key
                                        ? orderBy.direction === "asc"
                                            ? <ArrowUpwardIcon fontSize="inherit" />
                                            : <ArrowDownwardIcon fontSize="inherit" />
                                        : <UnfoldMoreIcon fontSize="inherit" />}
                                </button>
                            )}
                        </div>
                    ))}
                    {hasActions && (
                        <div className="px-3 py-3 font-semibold text-sm">Actions</div>
                    )}
                </div>

                {/* Body */}
                {loading && (
                    <div className="flex justify-center py-12" style={fullSpan}>
                        <LoadingSpinner width={32} height={32} />
                    </div>
                )}
                {!loading && error && (
                    <div className="flex flex-col items-center gap-3 py-12 text-sm text-grey" style={fullSpan}>
                        <span>{error}</span>
                        <button
                            onClick={runFetch}
                            className="px-4 py-1.5 rounded-md bg-light-light-grey hover:bg-light-grey text-dark-grey transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                )}
                {!loading && !error && displayItems.length === 0 && (
                    <div className="flex justify-center py-12 text-sm text-grey" style={fullSpan}>
                        {emptyState ?? "No results found."}
                    </div>
                )}
                {!loading && !error && displayItems.map(item => (
                    <DataRow
                        key={item.id}
                        item={item}
                        columns={visibleColumns}
                        actions={actions}
                        subgridRow={subgridRow}
                        hasBulkActions={hasBulkActions}
                        selected={selectedIDs.has(item.id)}
                        onSelect={toggleSelect}
                        onRowClick={onRowClick}
                        onAction={handleAction}
                        actionLoading={actionLoading}
                    />
                ))}
                {!loading && !error && padToLimit && displayItems.length > 0 && Array.from({ length: limit - displayItems.length }).map((_, i) => (
                    <div key={`pad-${i}`} className="grid border-b border-light-light-grey min-h-[45px]" style={subgridRow}>
                        {visibleColumns.map(col => <div key={col.key} className="px-3" />)}
                        {actions.length > 0 && <div className="px-3" />}
                    </div>
                ))}

                {/* Footer */}
                {!error && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-light-light-grey text-sm text-grey" style={fullSpan}>
                        {knownTotal ? (
                            <span>
                                {total === 0 ? "0 results" : `Showing ${startRow}–${endRow} of ${total}`}
                            </span>
                        ) : (
                            <span>{`Page ${page}`}</span>
                        )}
                        <Paginator
                            page={page}
                            totalPages={totalPages}
                            hasMore={hasMore}
                            onChange={setPage}
                            disabled={loading}
                        />
                    </div>
                )}
            </div>
        </div>

        {confirmState && (
            <Dialog
                title={typeof confirmState.action.confirm!.title === "function"
                    ? confirmState.action.confirm!.title(confirmState.row)
                    : confirmState.action.confirm!.title}
                description={typeof confirmState.action.confirm!.description === "function"
                    ? confirmState.action.confirm!.description(confirmState.row)
                    : confirmState.action.confirm!.description}
                confirmLabel={confirmState.action.confirm!.confirmLabel}
                variant={confirmState.action.confirm!.variant}
                error={confirmError}
                loading={confirmLoading}
                onConfirm={handleConfirm}
                onClose={() => { if (!confirmLoading) { setConfirmState(null); setConfirmError(null); } }}
            />
        )}
        </>
    );
}

// --- Sub-components ---

type DataRowProps<T extends HasID> = {
    item: T;
    columns: ColumnDef<T>[];
    actions: ActionDef<T>[];
    subgridRow: React.CSSProperties;
    hasBulkActions: boolean;
    selected: boolean;
    onSelect: (id: string) => void;
    onRowClick?: (row: T) => void;
    onAction: (action: ActionDef<T>, row: T, idx: number) => void;
    actionLoading: Record<string, boolean>;
};

function DataRow<T extends HasID>({
    item,
    columns,
    actions,
    subgridRow,
    hasBulkActions,
    selected,
    onSelect,
    onRowClick,
    onAction,
    actionLoading,
}: DataRowProps<T>) {
    const visibleActions = actions.filter(a => !a.hidden?.(item));
    const hasActions = visibleActions.length > 0;
    const isRowLoading = Object.keys(actionLoading).some(k => k.startsWith(item.id + "-"));

    return (
        <div
            className={`grid border-b border-light-light-grey transition-colors ${
                onRowClick ? "cursor-pointer hover:bg-light-light-grey/40" : ""
            } ${selected ? "bg-blue/5" : ""} ${isRowLoading ? "opacity-60" : ""}`}
            style={subgridRow}
            onClick={onRowClick ? () => onRowClick(item) : undefined}
        >
            {hasBulkActions && (
                <div
                    className="flex items-center justify-center p-3"
                    onClick={e => { e.stopPropagation(); onSelect(item.id); }}
                >
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => onSelect(item.id)}
                        className="cursor-pointer"
                    />
                </div>
            )}
            {columns.map(col => {
                const raw = (item as Record<string, unknown>)[col.key];
                return (
                    <div key={col.key} className="px-3 py-3 flex items-center text-sm min-w-0">
                        <span className="truncate">
                        {col.render ? col.render(raw, item) : String(raw ?? "")}
                        </span>
                    </div>
                );
            })}
            {hasActions && (
                <div
                    className="px-3 py-2 flex items-center gap-2"
                    onClick={e => e.stopPropagation()}
                >
                    {visibleActions.map((action, i) => {
                        const realIdx = actions.indexOf(action);
                        const key = `${item.id}-${realIdx}`;
                        const isLoading = actionLoading[key] ?? false;
                        const label = typeof action.label === "function" ? action.label(item) : action.label;
                        const icon = typeof action.icon === "function" ? action.icon(item) : action.icon;

                        if (action.variant === "icon") {
                            return (
                                <button
                                    key={i}
                                    title={label}
                                    disabled={isLoading}
                                    onClick={() => onAction(action, item, realIdx)}
                                    className="p-1 rounded text-grey hover:text-dark-grey hover:bg-light-light-grey disabled:opacity-50 transition-colors"
                                >
                                    {icon}
                                </button>
                            );
                        }

                        return (
                            <button
                                key={i}
                                disabled={isLoading}
                                onClick={() => onAction(action, item, realIdx)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium border border-light-grey text-grey hover:border-grey hover:text-dark-grey disabled:opacity-50 transition-colors"
                            >
                                {icon}
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function IndeterminateCheckbox({
    checked,
    indeterminate,
    onChange,
}: {
    checked: boolean;
    indeterminate: boolean;
    onChange: () => void;
}) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
        <input
            ref={ref}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            className="cursor-pointer"
        />
    );
}

function ColumnVisibilityToggle<T>({
    columns,
    hiddenKeys,
    onToggle,
}: {
    columns: ColumnDef<T>[];
    hiddenKeys: Set<string>;
    onToggle: (key: string) => void;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    return (
        <div ref={ref} className="relative flex-shrink-0">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-md border-2 border-light-light-grey bg-white text-sm text-grey hover:text-dark-grey transition-colors"
            >
                <ViewColumnOutlinedIcon fontSize="small" />
                Columns
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 bg-white shadow-lg rounded-md py-1 z-20 min-w-[160px] outline outline-1 outline-light-light-grey">
                    {columns.map(col => (
                        <label
                            key={col.key}
                            className={`flex items-center gap-2 px-3 py-2 text-sm ${col.required ? "opacity-50 cursor-not-allowed" : "hover:bg-light-light-grey cursor-pointer"}`}
                        >
                            <input
                                type="checkbox"
                                checked={!hiddenKeys.has(col.key)}
                                disabled={col.required}
                                onChange={() => onToggle(col.key)}
                            />
                            {col.label}
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function Paginator({
    page,
    totalPages,
    hasMore,
    onChange,
    disabled,
}: {
    page: number;
    totalPages?: number;
    hasMore?: boolean;
    onChange: (page: number) => void;
    disabled: boolean;
}) {
    const unknownTotal = totalPages === undefined;
    const canPrev = page > 1;
    const canNext = unknownTotal ? (hasMore ?? false) : page < (totalPages ?? 1);

    const btnClass = (enabled: boolean) =>
        `px-3 py-1 rounded border text-xs font-medium transition-colors ${
            enabled && !disabled
                ? "border-light-grey text-grey hover:border-grey hover:text-dark-grey"
                : "border-light-light-grey text-light-grey cursor-not-allowed"
        }`;

    return (
        <div className="flex items-center gap-1.5">
            {!unknownTotal && (
                <button
                    className={btnClass(page > 1 && !disabled)}
                    disabled={!canPrev || disabled}
                    onClick={() => onChange(1)}
                >
                    First
                </button>
            )}
            <button
                className={btnClass(canPrev && !disabled)}
                disabled={!canPrev || disabled}
                onClick={() => onChange(page - 1)}
            >
                Prev
            </button>
            {!unknownTotal && (
                <span className="px-2 text-xs text-grey">
                    Page {page} of {totalPages}
                </span>
            )}
            <button
                className={btnClass(canNext && !disabled)}
                disabled={!canNext || disabled}
                onClick={() => onChange(page + 1)}
            >
                Next
            </button>
            {!unknownTotal && (
                <button
                    className={btnClass(page < (totalPages ?? 1) && !disabled)}
                    disabled={!canNext || disabled}
                    onClick={() => onChange(totalPages ?? 1)}
                >
                    Last
                </button>
            )}
        </div>
    );
}
